import React from 'react';
import {
  Box, Paper, Typography, Chip, Stack, Stepper, Step, StepLabel, StepContent,
} from '@mui/material';
import { TrainingInsights, scoreColor } from '../types';

interface Props {
  data: TrainingInsights['salesIntelligence'];
}

/**
 * Training-only section. Replaces the "phase view" from post-call adherence
 * (which doesn't fit training — there's no organic opener/discovery/close).
 * Instead shows the ordered sequence of KB items the AI customer surfaced
 * and how the rep handled each, including how many pushback rounds were used.
 */
const KbItemProgressionSection: React.FC<Props> = ({ data }) => {
  if (!data) return null;
  const progression = data.kbItemProgression || [];

  return (
    <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mb: 2 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6">KB Item Progression</Typography>
        <Chip label={`${progression.length} item(s)`} size="medium" variant="outlined" />
      </Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Order in which the AI customer surfaced each knowledge-base item, how
        the rep handled it, and how many pushback rounds were spent before
        moving on.
      </Typography>

      {progression.length === 0 ? (
        <Typography variant="body2" color="text.secondary" fontStyle="italic">
          No KB items were surfaced during this session.
        </Typography>
      ) : (
        <Stepper orientation="vertical" nonLinear activeStep={-1}>
          {progression.map((item, idx) => {
            // `not_attempted` is set by the analyzer when the session was
            // abandoned and the rep never engaged with this item. Render it
            // distinctly (grey, no score) — it's not a "missed" score 0,
            // it's just an item the rep didn't get to.
            const isNotAttempted = item.status === 'not_attempted';
            return (
              <Step key={`${item.trackItemId}-${idx}`} expanded>
                <StepLabel
                  StepIconProps={{
                    icon: isNotAttempted ? (
                      <Chip label="—" size="small" variant="outlined" sx={{ opacity: 0.6 }} />
                    ) : (
                      <Chip
                        label={`${item.handlingScore}%`}
                        size="small"
                        color={scoreColor(item.handlingScore)}
                      />
                    ),
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap"
                       sx={{ opacity: isNotAttempted ? 0.7 : 1 }}>
                    <Chip
                      label={item.itemType}
                      size="small"
                      color="info"
                      variant="outlined"
                    />
                    {isNotAttempted && (
                      <Chip
                        label="not attempted"
                        size="small"
                        variant="outlined"
                        sx={{ color: 'text.secondary' }}
                      />
                    )}
                    <Typography variant="subtitle2" component="span">
                      {item.question || '(no question text)'}
                    </Typography>
                    {!isNotAttempted && item.pushbackRoundsUsed > 0 && (
                      <Chip
                        label={`${item.pushbackRoundsUsed} pushback${item.pushbackRoundsUsed > 1 ? 's' : ''}`}
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </StepLabel>
                <StepContent>
                  {isNotAttempted ? (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Session ended before the rep engaged with this item — no score assigned.
                    </Typography>
                  ) : (
                    <>
                      {item.oneLineSummary && (
                        <Typography variant="body2" color="text.secondary">
                          {item.oneLineSummary}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                        Surfaced on turn {item.turnSurfaced}
                        {item.turnAddressed !== null && item.turnAddressed !== undefined
                          ? ` · addressed on turn ${item.turnAddressed}`
                          : ' · never explicitly addressed'}
                      </Typography>
                    </>
                  )}
                </StepContent>
              </Step>
            );
          })}
        </Stepper>
      )}

      {/* Playbook phase progression — only meaningful when playbook is enabled */}
      {data.playbookPhaseProgression && data.playbookPhaseProgression.length > 0 && (
        <Box mt={3}>
          <Typography variant="subtitle1" gutterBottom>
            Playbook Phase Progression
          </Typography>
          <Stack spacing={1}>
            {data.playbookPhaseProgression.map((phase, idx) => (
              <Paper key={idx} variant="outlined" sx={{ p: 1.5 }}>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <Typography variant="subtitle2" textTransform="capitalize">
                    {phase.phase.replace(/([A-Z])/g, ' $1').trim()}
                  </Typography>
                  <Chip
                    label={phase.wasDelivered ? 'delivered' : 'not delivered'}
                    size="small"
                    color={phase.wasDelivered ? 'success' : 'default'}
                    variant="outlined"
                  />
                  <Chip
                    label={`${phase.coverageScore}%`}
                    size="small"
                    color={scoreColor(phase.coverageScore)}
                  />
                </Box>
                {phase.oneLineSummary && (
                  <Typography variant="body2" color="text.secondary">
                    {phase.oneLineSummary}
                  </Typography>
                )}
                {phase.missedPoints?.length > 0 && (
                  <Typography variant="caption" color="error.main" display="block" mt={0.5}>
                    Missed: {phase.missedPoints.join(' • ')}
                  </Typography>
                )}
              </Paper>
            ))}
          </Stack>
        </Box>
      )}

      {/* Buying signals missed + coaching */}
      {data.buyingSignalsMissed && data.buyingSignalsMissed.length > 0 && (
        <Box mt={3}>
          <Typography variant="subtitle1" gutterBottom>
            Buying Signals Missed
          </Typography>
          <Stack spacing={1}>
            {data.buyingSignalsMissed.map((bs, idx) => (
              <Paper key={idx} variant="outlined" sx={{ p: 1.5, bgcolor: 'warning.50' }}>
                <Typography variant="subtitle2" color="warning.dark">
                  {bs.signal}
                </Typography>
                {bs.repResponse && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Rep said: "{bs.repResponse}"
                  </Typography>
                )}
                {bs.suggestion && (
                  <Typography variant="body2" mt={0.5}>
                    Suggestion: {bs.suggestion}
                  </Typography>
                )}
              </Paper>
            ))}
          </Stack>
        </Box>
      )}
    </Paper>
  );
};

export default KbItemProgressionSection;
